import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User } from '@/types'

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  lastActivity: number
  
  // Actions
  login: (user: User) => void
  logout: () => void
  updateUser: (user: Partial<User>) => void
  setLoading: (loading: boolean) => void
  updateActivity: () => void
  
  // Getters
  hasRole: (role: string) => boolean
  isAdmin: () => boolean
  isSessionExpired: () => boolean
}

const SESSION_TIMEOUT = 30 * 60 * 1000 // 30 minutes

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      lastActivity: Date.now(),
      
      login: (user: User) => {
        set({
          user,
          isAuthenticated: true,
          isLoading: false,
          lastActivity: Date.now(),
        })
      },
      
      logout: () => {
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          lastActivity: Date.now(),
        })

        // Clear tenant context on logout
        import('./tenantStore').then(({ useTenantStore }) => {
          useTenantStore.getState().clearTenant()
        }).catch(error => {
          console.error('Failed to clear tenant context on logout:', error)
        })
      },
      
      updateUser: (userData: Partial<User>) => {
        const { user } = get()
        if (user) {
          set({
            user: { ...user, ...userData },
            lastActivity: Date.now(),
          })
        }
      },
      
      setLoading: (loading: boolean) => {
        set({ isLoading: loading })
      },
      
      updateActivity: () => {
        set({ lastActivity: Date.now() })
      },
      
      hasRole: (role: string) => {
        const { user } = get()
        return user?.role === role
      },
      
      isAdmin: () => {
        const { user } = get()
        return user?.role === 'admin'
      },
      
      isSessionExpired: () => {
        const { lastActivity } = get()
        return Date.now() - lastActivity > SESSION_TIMEOUT
      },
    }),
    {
      name: 'bonnycloud-auth',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        lastActivity: state.lastActivity,
      }),
    }
  )
)